import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import config from '../config';
import { useLanguage } from '../contexts/LanguageContext';
import { useTranslation } from '../hooks/useTranslation';
import { fetchWithLanguage } from '../utils/api';
import { trackEvent } from '../../utils/Analytics';

export function DiagnosisDetail({ diagnosis, abnormalResults, onClose }) {
    const { language } = useLanguage();
    const [loading, setLoading] = useState(true);
    const [details, setDetails] = useState(null);
    const [error, setError] = useState(null);
    const [readingProgress, setReadingProgress] = useState(0);
    const [showScrollTop, setShowScrollTop] = useState(false);
    const contentRef = useRef(null);
    const hasRequestedRef = useRef(false);
    const { t } = useTranslation();
    const viewStartTime = useRef(Date.now());
    const [activeSection, setActiveSection] = useState('explanation-section');
    const sectionRefs = useRef({
        'explanation-section': useRef(null),
        'symptoms-section': useRef(null),
        'medications-section': useRef(null),
        'recommendations-section': useRef(null)
    });

    // Memoize the request payload
    const requestPayload = useMemo(() => ({
        diagnosis,
        abnormalResults: Array.isArray(abnormalResults) ? abnormalResults : []
    }), [diagnosis, abnormalResults]);

    // Track initial view
    useEffect(() => {
        trackEvent('Diagnosis Detail', 'view_detail', 'Diagnosis Detail Viewed', {
            condition: diagnosis.condition,
            severity: diagnosis.severity,
            likelihood: diagnosis.likelihood
        });

        // Track view duration when component unmounts
        return () => {
            const duration = Math.round((Date.now() - viewStartTime.current) / 1000);
            trackEvent('Diagnosis Detail', 'detail_view_duration', 'Time Spent Viewing Detail', duration);
        };
    }, [diagnosis]);

    // Memoize the fetch function
    const fetchData = useCallback(async () => {
        if (hasRequestedRef.current) return;
        hasRequestedRef.current = true;

        setLoading(true);
        setError(null);

        try {
            console.log('Sending request with:', requestPayload);

            const response = await fetchWithLanguage(`${config.apiUrl}/diagnosis/details`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(requestPayload),
            });

            if (!response.ok) {
                const errorText = await response.text();
                console.error('Server response:', response.status, errorText);
                let errorMessage;
                try {
                    const errorData = JSON.parse(errorText);
                    errorMessage = errorData.error;
                } catch (e) {
                    errorMessage = `Server error: ${response.status}`;
                }
                throw new Error(errorMessage);
            }

            const data = await response.json();
            setDetails(data);
        } catch (err) {
            setError(err.message || 'Failed to load additional information. Please try again.');
            console.error('Error fetching diagnosis details:', err);
        } finally {
            setLoading(false);
        }
    }, [requestPayload, language]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    // Function to scroll to a specific section
    const scrollToSection = (sectionId) => {
        const section = document.getElementById(sectionId);
        if (section && contentRef.current) {
            const container = contentRef.current;
            const sectionTop = section.offsetTop - container.offsetTop;
            container.scrollTo({
                top: sectionTop,
                behavior: 'smooth'
            });
            setActiveSection(sectionId);
        }
    };

    // Set up intersection observer to track which section is in view
    useEffect(() => {
        if (!loading && contentRef.current) {
            const options = {
                root: contentRef.current,
                rootMargin: '-10% 0px -70% 0px', // Consider a section in view when it's 10% from the top and 70% from the bottom
                threshold: 0
            };

            const observer = new IntersectionObserver((entries) => {
                entries.forEach(entry => {
                    if (entry.isIntersecting) {
                        const sectionId = entry.target.id;
                        setActiveSection(sectionId);
                    }
                });
            }, options);

            // Observe all section elements
            const sections = contentRef.current.querySelectorAll('[data-section]');
            sections.forEach(section => {
                observer.observe(section);
            });

            return () => {
                sections.forEach(section => {
                    observer.unobserve(section);
                });
            };
        }
    }, [loading]);

    // Enhanced scroll handler with analytics and section tracking
    const handleScroll = (e) => {
        const element = e.target;
        const scrollTop = element.scrollTop;
        const scrollHeight = element.scrollHeight - element.clientHeight;
        const progress = (scrollTop / scrollHeight) * 100;
        const newProgress = Math.min(Math.round(progress), 100);

        setReadingProgress(newProgress);
        setShowScrollTop(scrollTop > 300);

        // Track when user reaches important milestones
        if (newProgress >= 25 && readingProgress < 25) {
            trackEvent('Diagnosis Detail', 'reading_progress', '25% Read', {
                condition: diagnosis.condition
            });
        }
        if (newProgress >= 75 && readingProgress < 75) {
            trackEvent('Diagnosis Detail', 'reading_progress', '75% Read', {
                condition: diagnosis.condition
            });
        }
        if (newProgress === 100 && readingProgress < 100) {
            trackEvent('Diagnosis Detail', 'reading_progress', 'Completed Reading', {
                condition: diagnosis.condition
            });
        }

        // Update active section based on scroll position
        if (!loading && details) {
            const sections = ['toc-section', 'explanation-section', 'symptoms-section', 'medications-section', 'recommendations-section'];

            // Find the section that is most visible in the viewport
            for (const sectionId of sections) {
                const section = document.getElementById(sectionId);
                if (section) {
                    const rect = section.getBoundingClientRect();
                    // If the section is in view
                    if (rect.top <= 150 && rect.bottom >= 150) {
                        if (activeSection !== sectionId) {
                            setActiveSection(sectionId);
                        }
                        break;
                    }
                }
            }
        }
    };

    const scrollToTop = () => {
        trackEvent('Diagnosis Detail', 'scroll_to_top', 'Back to Top Clicked');
        contentRef.current?.scrollTo({
            top: 0,
            behavior: 'smooth'
        });
    };

    // Track when sections become visible
    useEffect(() => {
        if (!loading && details) {
            const observer = new IntersectionObserver(
                (entries) => {
                    entries.forEach(entry => {
                        if (entry.isIntersecting) {
                            const sectionName = entry.target.getAttribute('data-section');
                            if (sectionName) {
                                trackEvent('Diagnosis Detail', 'view_section', `Section Viewed: ${sectionName}`, {
                                    condition: diagnosis.condition,
                                    section: sectionName
                                });
                            }
                            observer.unobserve(entry.target); // Only track first view
                        }
                    });
                },
                { threshold: 0.5 }
            );

            // Observe each main section
            document.querySelectorAll('[data-section]').forEach(section => {
                observer.observe(section);
            });

            return () => observer.disconnect();
        }
    }, [loading, details, diagnosis]);

    // Click outside to close
    const handleBackdropClick = (e) => {
        if (e.target === e.currentTarget) {
            onClose();
        }
    };

    if (error) {
        return (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
                onClick={handleBackdropClick}>
                <div className="bg-white rounded-xl p-6 max-w-lg w-full">
                    <div className="flex justify-between items-center mb-4">
                        <h3 className="text-xl font-semibold text-red-600">{t('diagnosis.error.title')}</h3>
                        <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>
                    <div className="text-red-600">{error}</div>
                </div>
            </div>
        );
    }

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4 overflow-y-auto"
            onClick={handleBackdropClick}>
            <div className="bg-white rounded-xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
                <div className="flex justify-between items-center p-6 border-b border-gray-200">
                    <div>
                        <h3 className="text-2xl font-bold text-indigo-900">
                            {diagnosis.condition}
                        </h3>
                        <div className="flex gap-2 mt-2">
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${getLikelihoodStyle(diagnosis.likelihood)}`}>
                                {diagnosis.likelihood}
                            </span>
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${getSeverityStyle(diagnosis.severity)}`}>
                                {diagnosis.severity}
                            </span>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="text-gray-500 hover:text-gray-700"
                    >
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                {loading ? (
                    <div className="flex flex-col items-center justify-center py-12">
                        <div className="animate-spin rounded-full h-12 w-12 border-4 border-indigo-200 border-t-indigo-600
                            [animation-duration:1.5s]"></div>
                        <p className="mt-4 text-indigo-600 animate-pulse">{t('diagnosis.loading')}</p>
                    </div>
                ) : (
                    <div className="flex flex-col md:flex-row h-[calc(90vh-120px)]">
                        {/* Left column - Table of Contents */}
                        <div className="md:w-1/4 md:border-r border-gray-200 p-4 overflow-y-auto">
                            <div id="toc-section" className="sticky top-0">
                                <h4 className="text-lg font-semibold text-gray-800 mb-3 border-b border-gray-200 pb-2">
                                    {t('diagnosis.sections.tableOfContents', { fallback: 'Table of Contents' })}
                                </h4>
                                <ul className="space-y-2">
                                    <li>
                                        <button
                                            onClick={() => scrollToSection('explanation-section')}
                                            className={`text-left w-full px-3 py-2 rounded-md transition-colors ${activeSection === 'explanation-section'
                                                ? 'bg-indigo-100 text-indigo-700 font-medium'
                                                : 'hover:bg-gray-100 text-gray-700'
                                                }`}
                                        >
                                            1. {t('diagnosis.sections.understanding', { fallback: 'Understanding Your Condition' })}
                                        </button>
                                    </li>
                                    <li>
                                        <button
                                            onClick={() => scrollToSection('symptoms-section')}
                                            className={`text-left w-full px-3 py-2 rounded-md transition-colors ${activeSection === 'symptoms-section'
                                                ? 'bg-indigo-100 text-indigo-700 font-medium'
                                                : 'hover:bg-gray-100 text-gray-700'
                                                }`}
                                        >
                                            2. {t('diagnosis.sections.symptoms', { fallback: 'Related Symptoms' })}
                                        </button>
                                    </li>
                                    <li>
                                        <button
                                            onClick={() => scrollToSection('medications-section')}
                                            className={`text-left w-full px-3 py-2 rounded-md transition-colors ${activeSection === 'medications-section'
                                                ? 'bg-indigo-100 text-indigo-700 font-medium'
                                                : 'hover:bg-gray-100 text-gray-700'
                                                }`}
                                        >
                                            3. {t('diagnosis.sections.medications', { fallback: 'Medications' })}
                                        </button>
                                    </li>
                                    <li>
                                        <button
                                            onClick={() => scrollToSection('recommendations-section')}
                                            className={`text-left w-full px-3 py-2 rounded-md transition-colors ${activeSection === 'recommendations-section'
                                                ? 'bg-indigo-100 text-indigo-700 font-medium'
                                                : 'hover:bg-gray-100 text-gray-700'
                                                }`}
                                        >
                                            4. {t('diagnosis.sections.management', { fallback: 'Management & Recommendations' })}
                                        </button>
                                    </li>
                                </ul>
                            </div>
                        </div>

                        {/* Right column - Content Sections */}
                        <div
                            ref={contentRef}
                            className="md:w-3/4 p-6 space-y-6 overflow-y-auto"
                        >
                            {/* Explanation Section */}
                            <div id="explanation-section" data-section="explanation" className="bg-indigo-50/70 rounded-lg p-6 relative">
                                <div className="flex justify-between items-center mb-4">
                                    <h4 className="text-xl font-semibold text-indigo-900 border-b border-indigo-200 pb-2">
                                        1. {t('diagnosis.understanding').replace('{condition}', diagnosis.condition)}
                                    </h4>
                                </div>
                                <div className="space-y-6">
                                    <div>
                                        <h5 className="font-bold text-indigo-600 mb-2 text-sm uppercase tracking-wide">{t('diagnosis.sections.overview')}</h5>
                                        <p className="text-indigo-900 leading-relaxed">
                                            {details.explanation.overview}
                                        </p>
                                    </div>
                                    <div>
                                        <h5 className="font-bold text-indigo-600 mb-2 text-sm uppercase tracking-wide">{t('diagnosis.sections.labSignificance')}</h5>
                                        <p className="text-indigo-900 leading-relaxed">
                                            {details.explanation.labResults}
                                        </p>
                                    </div>
                                    <div>
                                        <h5 className="font-bold text-indigo-600 mb-2 text-sm uppercase tracking-wide">{t('diagnosis.sections.healthRisks')}</h5>
                                        <p className="text-indigo-900 leading-relaxed">
                                            {details.explanation.risks}
                                        </p>
                                    </div>
                                    {details.explanation.mechanism && (
                                        <div>
                                            <h5 className="font-bold text-indigo-600 mb-2 text-sm tracking-wide uppercase">{t('diagnosis.sections.bodyMechanism')}</h5>
                                            <p className="text-indigo-900 leading-relaxed">
                                                {details.explanation.mechanism}
                                            </p>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Symptoms Section */}
                            <div id="symptoms-section" data-section="symptoms" className="bg-indigo-50/70 rounded-lg p-6 relative">
                                <div className="flex justify-between items-center mb-4">
                                    <h4 className="text-xl font-semibold text-indigo-900 border-b border-indigo-200 pb-2">
                                        2. {t('diagnosis.sections.symptoms', { fallback: 'Related Symptoms' })}
                                    </h4>
                                </div>
                                <div className="space-y-6">
                                    {details.relatedSymptoms.map((category, index) => {
                                        // Create a safe translation key from the category name
                                        const categoryKey = category.category.toLowerCase()
                                            .replace(/\s+/g, '') // Remove spaces
                                            .replace(/[^a-z0-9]/g, ''); // Remove special characters

                                        return (
                                            <div key={index} className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
                                                <h5 className="font-semibold text-gray-900 mb-3 text-sm uppercase tracking-wide">
                                                    {/* Fallback to original category name if translation is missing */}
                                                    {t(`diagnosis.sections.${categoryKey}`, { fallback: category.category })}
                                                </h5>
                                                <ul className="space-y-2">
                                                    {category.symptoms.map((symptom, idx) => (
                                                        <li key={idx} className="flex items-start">
                                                            <span className="mr-2 text-indigo-500">•</span>
                                                            <span className="text-gray-700">{symptom}</span>
                                                        </li>
                                                    ))}
                                                </ul>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>

                            {/* Medications Section */}
                            <div id="medications-section" data-section="medications" className="bg-indigo-50/70 rounded-lg p-6 relative">
                                <div className="flex justify-between items-center mb-4">
                                    <h4 className="text-xl font-semibold text-indigo-900 border-b border-indigo-200 pb-2">
                                        3. {t('diagnosis.sections.medications', { fallback: 'Medications' })}
                                    </h4>
                                </div>
                                <div className="space-y-6">
                                    {(details.medications?.otc?.length > 0 || details.medications?.prescription?.length > 0) ? (
                                        <div>
                                            {details.medications?.otc?.length > 0 && (
                                                <div>
                                                    <h5 className="font-medium text-blue-800 mb-2">{t('diagnosis.sections.otcMedications', { fallback: 'Over-the-counter' })}</h5>
                                                    <ul className="space-y-2 bg-white bg-opacity-70 p-3 rounded-lg">
                                                        {details.medications.otc.map((med, idx) => (
                                                            <li key={idx} className="flex items-start">
                                                                <span className="mr-2 text-blue-500">•</span>
                                                                <span className="text-blue-800">{med}</span>
                                                            </li>
                                                        ))}
                                                    </ul>
                                                </div>
                                            )}

                                            {details.medications?.prescription?.length > 0 && (
                                                <div>
                                                    <h5 className="font-medium text-blue-800 mb-2">{t('diagnosis.sections.prescriptionMedications', { fallback: 'Prescription medications' })}</h5>
                                                    <div className="bg-white bg-opacity-50 p-3 rounded-lg border border-blue-100">
                                                        <p className="text-xs text-blue-600 mb-2 italic">
                                                            {t('diagnosis.sections.prescriptionDisclaimer', { fallback: 'The following medications may be prescribed by your doctor. Never self-medicate with prescription drugs.' })}
                                                        </p>
                                                        <ul className="space-y-2">
                                                            {details.medications.prescription.map((med, idx) => (
                                                                <li key={idx} className="flex items-start">
                                                                    <span className="mr-2 text-blue-500">•</span>
                                                                    <span className="text-blue-800">{med}</span>
                                                                </li>
                                                            ))}
                                                        </ul>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    ) : (
                                        <p className="text-blue-800 italic">
                                            {t('diagnosis.sections.noMedications', { fallback: 'No specific medications are listed for this condition.' })}
                                        </p>
                                    )}
                                </div>
                            </div>

                            {/* Recommendations Section */}
                            <div id="recommendations-section" data-section="recommendations" className="bg-indigo-50/70 rounded-lg p-6 relative">
                                <div className="flex justify-between items-center mb-4">
                                    <h4 className="text-xl font-semibold text-indigo-900 border-b border-indigo-200 pb-2">
                                        4. {t('diagnosis.sections.management', { fallback: 'Management & Recommendations' })}
                                    </h4>
                                </div>
                                <div className="space-y-6">
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div>
                                            <h5 className="font-medium text-green-800 mb-2">{t('diagnosis.sections.lifestyle')}</h5>
                                            <ul className="space-y-2">
                                                {details.recommendations.lifestyle.map((item, idx) => (
                                                    <li key={idx} className="flex items-start">
                                                        <span className="mr-2">✓</span>
                                                        <span className="text-green-900">{item}</span>
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                        <div>
                                            <h5 className="font-medium text-green-800 mb-2">{t('diagnosis.sections.monitoring')}</h5>
                                            <ul className="space-y-2">
                                                {details.recommendations.monitoring.map((item, idx) => (
                                                    <li key={idx} className="flex items-start">
                                                        <span className="mr-2">•</span>
                                                        <span className="text-green-900">{item}</span>
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    </div>

                                    <div className="mt-4 space-y-3">
                                        <div className="bg-green-100 p-3 rounded">
                                            <h5 className="font-medium text-green-800 mb-1">{t('diagnosis.sections.followUpCare')}</h5>
                                            <p className="text-green-900">{details.recommendations.followUp}</p>
                                        </div>

                                        <div className="bg-red-50 p-3 rounded">
                                            <h5 className="font-medium text-red-800 mb-1">{t('diagnosis.sections.urgentCare')}</h5>
                                            <p className="text-red-900">{details.recommendations.urgentCare}</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

function getLikelihoodStyle(likelihood) {
    switch (likelihood?.toLowerCase()) {
        case 'likely':
            return 'bg-purple-100 text-purple-800';
        case 'possible':
            return 'bg-blue-100 text-blue-800';
        case 'unlikely':
            return 'bg-gray-100 text-gray-800';
        default:
            return 'bg-gray-100 text-gray-800';
    }
}

function getSeverityStyle(severity) {
    switch (severity?.toLowerCase()) {
        case 'severe':
            return 'bg-red-100 text-red-800';
        case 'moderate':
            return 'bg-orange-100 text-orange-800';
        case 'mild':
            return 'bg-yellow-100 text-yellow-800';
        default:
            return 'bg-gray-100 text-gray-800';
    }
} 